import React, { useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { dialogs, i18next, customization } from '@yola/ws-sdk';
import AddFontPairDialog from '../components/add-font-pair-dialog';
import dialogTypes from '../../dialogs/constants/dialog-types';
import helpers from '../helpers';
import constants from '../constants';
import dialogCallSourceIds from '../constants/dialog-call-source-ids';
import { setFontOptionVisible } from '../helpers/font-options-visibility-manager';
import useFontsData from '../helpers/use-fonts-data';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common';

const {
  generateUniqueFontPairId,
  linkFont,
  getFontFallbackByCategory,
  prepareFontVariantsString,
  getCompiledFontPairConfig,
} = helpers;
const { DEFAULT_OFFSET_X, DEFAULT_OFFSET_Y } = constants;
const SELECT_CLASS_NAME = 'ws-ui-base-select';

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Fonts')],
  title: i18next.t('Add your font pair'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t(
    'Select your primary and secondary fonts that will be applied across your website'
  ),
  primaryFontTitle: i18next.t('Primary font'),
  secondaryFontTitle: i18next.t('Secondary font'),
  noResultsText: i18next.t('No result'),
  primaryFontPopover: i18next.t('Applies to headings 1–4'),
  secondaryFontPopover: i18next.t('Applies to headings 5–6 and paragraph'),
});

const prepareOptions = (fontsData) =>
  fontsData.map(({ family }) => {
    const value = family.replace(/ /g, '+');

    return { value, label: family };
  });

const formatFontId = (value) => value.toLowerCase().replace(/ /g, '-');

const AddFontPairDialogContainer = ({
  onDialogMainAction,
  onDialogCancel,
  getSharedData,
  resolveSharedData,
}) => {
  const { data: fontsData, isSuccess } = useFontsData();
  const isReady = isSuccess && fontsData.length > 0;

  const {
    activeFontPairId,
    customFonts,
    allFonts,
    applyCustomFontPair,
    offsetX = DEFAULT_OFFSET_X,
    offsetY = DEFAULT_OFFSET_Y,
  } = getSharedData();

  const captions = getCaptions();
  const fontsOptions = useMemo(() => prepareOptions(fontsData), [fontsData]);

  // Keep the latest fonts data in a ref so the stable IntersectionObserver below reads the current
  // data instead of the empty array captured on the first render (fonts load lazily now).
  const fontsDataRef = useRef(fontsData);
  fontsDataRef.current = fontsData;
  const currentFontPair = allFonts.find(({ id }) => id === activeFontPairId);
  const compiledCurrentFontPair = currentFontPair.isCustom
    ? getCompiledFontPairConfig(currentFontPair)
    : currentFontPair;
  const {
    variables: { primaryFontFamily, secondaryFontFamily },
  } = compiledCurrentFontPair;

  // Some of the used fonts may be deprecated by now, so fall back to the first available option
  // when the used font is absent from the Google fonts list.
  const defaultPrimaryFontOption = useMemo(
    () => fontsOptions.find((option) => option.label === primaryFontFamily) || fontsOptions[0],
    [fontsOptions, primaryFontFamily]
  );
  const defaultSecondaryFontOption = useMemo(
    () => fontsOptions.find((option) => option.label === secondaryFontFamily) || fontsOptions[0],
    [fontsOptions, secondaryFontFamily]
  );

  // Current selection, seeded from the defaults once fonts data is ready and updated on change.
  const primaryFont = useRef(null);
  const secondaryFont = useRef(null);
  const newFontPairConfig = useRef({});

  const [dialogPosition, setDialogPosition] = useState({
    offsetX,
    offsetY,
  });

  const generateFontPairConfig = () => {
    const primaryFontId = formatFontId(primaryFont.current.label);
    const secondaryFontId = formatFontId(secondaryFont.current.label);
    const baseId = `${primaryFontId}_${secondaryFontId}`;
    const uniqueId = generateUniqueFontPairId(baseId);

    const { category: primaryFontCategory, variants: primaryFontVariants } = fontsData.find(
      ({ family }) => family === primaryFont.current.label
    );
    const { category: secondaryFontCategory, variants: secondaryFontVariants } = fontsData.find(
      ({ family }) => family === secondaryFont.current.label
    );

    const primaryFontTypeFallback = getFontFallbackByCategory(primaryFontCategory);
    const secondaryFontTypeFallback = getFontFallbackByCategory(secondaryFontCategory);

    return {
      id: uniqueId,
      parentId: customization.constants.FALLBACK_FONT_PAIR_ID,
      label: `${primaryFont.current.label} + ${secondaryFont.current.label}`,
      isCustom: true,
      variableOverrides: {
        primaryFontFamily: primaryFont.current.label,
        secondaryFontFamily: secondaryFont.current.label,
        primaryFontTypeFallback,
        secondaryFontTypeFallback,
        usedFonts: `${primaryFont.current.value}:${prepareFontVariantsString(
          primaryFontVariants
        )} | ${secondaryFont.current.value}:${prepareFontVariantsString(secondaryFontVariants)}`,
      },
    };
  };

  const onPrimaryFontChange = (font) => {
    primaryFont.current = font;
    newFontPairConfig.current = generateFontPairConfig();
    applyCustomFontPair({ fontPairConfig: newFontPairConfig.current });
  };

  const onSecondaryFontChange = (font) => {
    secondaryFont.current = font;
    newFontPairConfig.current = generateFontPairConfig();
    applyCustomFontPair({ fontPairConfig: newFontPairConfig.current });
  };

  const updateSharedState = (customData) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...customData,
    });
  };

  const handleSubmit = (event) => {
    updateSharedState({
      customFonts: [...customFonts, newFontPairConfig.current],
      activeFontPairId: newFontPairConfig.current.id,
    });

    const newUsedFonts = Array.from(
      new Set([primaryFont.current.label, secondaryFont.current.label])
    );

    newUsedFonts.forEach((font) => {
      linkFont(font);
    });

    onDialogMainAction({
      triggerId: event ? SUBMIT_BUTTON_TRIGGER_ID : DIALOG_BACKGROUND_TRIGGER_ID,
      primaryFont: primaryFont.current.label,
      secondaryFont: secondaryFont.current.label,
    });

    dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_FONTS_DIALOG, {
      sourceId: dialogCallSourceIds.ADD_FONT_PAIR,
    });
  };

  const handleCancel = () => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
    });

    applyCustomFontPair({ fontPairId: activeFontPairId });

    onDialogCancel();

    dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_FONTS_DIALOG, {
      sourceId: dialogCallSourceIds.ADD_FONT_PAIR,
    });
  };

  const handleDragEnd = (_, { x, y }) => {
    setDialogPosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const observer = useMemo(() => {
    const root = document.querySelector(`.${SELECT_CLASS_NAME}`);

    return new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            const fontFamily = target.id;
            setFontOptionVisible(fontFamily);
            const font = fontsDataRef.current.find(({ family }) => family === fontFamily);
            linkFont(font.family);
            obs.unobserve(target);
          }
        });
      },
      {
        root,
        rootMargin: '1000px',
        scrollMargin: '1000px',
      }
    );
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => observer.disconnect(), [observer]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    primaryFont.current = defaultPrimaryFontOption;
    secondaryFont.current = defaultSecondaryFontOption;
    newFontPairConfig.current = generateFontPairConfig();
    applyCustomFontPair({ fontPairConfig: newFontPairConfig.current });

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [isReady]);

  return (
    <AddFontPairDialog
      isLoading={!isReady}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      fontsOptions={fontsOptions}
      fontsData={fontsData}
      defaultOptions={{
        primaryFont: defaultPrimaryFontOption,
        secondaryFont: defaultSecondaryFontOption,
      }}
      fontObserver={observer}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onPrimaryFontChange={onPrimaryFontChange}
      onSecondaryFontChange={onSecondaryFontChange}
      onDragEnd={handleDragEnd}
    />
  );
};

AddFontPairDialogContainer.propTypes = {
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default AddFontPairDialogContainer;
